import React, { useState } from 'react';
import Swal from 'sweetalert2';
import apiClient from '../../services/api/apiClient.js';
import Modal from '../../components/common/Modal.jsx';

/**
 * Grupos, etiquetas y ORDEN idénticos al modal "Permisos de {nombre} {apellido}" de
 * `IbuFront/src/components/layout/Login/ListadoUsuarios.jsx` (secciones CATALOGOS /
 * CONTENEDORES / CARGOS / ADMIN / RETIROS / GASTOS / PAGOS / REPORTES / USUARIOS / LOGS /
 * ASISTENTE IA, 4 por fila). V1 NO tiene checkbox para `usuarios_create`,
 * `usuarios_permisos`, `iaFinanzasSocios` ni `iaComisiones`, así que tampoco se muestran
 * aquí (auditoría 2026-10-05). Los nombres de flag son los reales de
 * `IbuBack2/src/core/auth/permisos.map.js`.
 */
const GRUPOS = [
  {
    titulo: 'CATALOGOS',
    flags: [
      { flag: 'menuCatalogos', label: 'Menu Catalogos' },
      { flag: 'menuClientes', label: 'Clientes' },
      { flag: 'clientesCreate', label: 'Crear Clientes' },
      { flag: 'menuArticulos', label: 'Articulos' },
      { flag: 'menuNavieras', label: 'Navieras' },
      { flag: 'menuProveedores', label: 'Proveedores' },
      { flag: 'menuEmpresas', label: 'Empresas' },
    ],
  },
  {
    titulo: 'CONTENEDORES',
    flags: [
      { flag: 'menuContenedores', label: 'Menu Contenedores' },
      { flag: 'contenedoresCreate', label: 'Crear Contenedores' },
      { flag: 'contenedoresEntrega', label: 'Entrega de Contenedores' },
      { flag: 'menuVentas', label: 'Ventas' },
    ],
  },
  {
    titulo: 'CARGOS',
    flags: [
      { flag: 'menuCargos', label: 'Menu Cargos' },
      { flag: 'cargosCreate', label: 'Crear Cargos' },
    ],
  },
  {
    titulo: 'ADMIN',
    flags: [
      { flag: 'menuAdmin', label: 'Menu Admin' },
      { flag: 'menuAbonos', label: 'Menu Abonos' },
      { flag: 'abonosCreate', label: 'Crear Abonos' },
      { flag: 'menuBancos', label: 'Bancos' },
      { flag: 'menuAnticipos', label: 'Menu Anticipos' },
      { flag: 'anticiposCreate', label: 'Crear Anticipos' },
      { flag: 'menuDevoluciones', label: 'Menu Devoluciones' },
      { flag: 'devolucionesCreate', label: 'Crear Devoluciones' },
      { flag: 'estadoCuentaClientes', label: 'Estado Cuenta Clientes' },
    ],
  },
  {
    titulo: 'RETIROS',
    flags: [
      { flag: 'menuRetiros', label: 'Menu Retiros' },
      { flag: 'retirosCreate', label: 'Crear Retiros' },
      { flag: 'menuSocios', label: 'Socios' },
    ],
  },
  {
    titulo: 'GASTOS',
    flags: [
      { flag: 'menu_gastos', label: 'Menu Gastos' },
      { flag: 'gastosCreate', label: 'Crear Gastos' },
      { flag: 'gastosPagar', label: 'Pagar Gastos' },
    ],
  },
  {
    titulo: 'PAGOS',
    flags: [
      { flag: 'menuPagos', label: 'Menu Pagos' },
      { flag: 'pagosCreate', label: 'Crear Pagos' },
      { flag: 'estadoCuentaProveedores', label: 'Estado Cuenta Proveedores' },
    ],
  },
  {
    titulo: 'REPORTES',
    flags: [
      { flag: 'menuReportes', label: 'Menu Reportes' },
      { flag: 'contenedoresReporte', label: 'Reporte Contenedores' },
      { flag: 'transferenciasReporte', label: 'Reporte Transferencias' },
      { flag: 'facturasReporte', label: 'Reporte Facturas' },
    ],
  },
  {
    titulo: 'USUARIOS',
    flags: [{ flag: 'menu_usuarios', label: 'Menu Usuarios' }],
  },
  {
    titulo: 'LOGS',
    flags: [{ flag: 'menu_logs', label: 'Menu Logs' }],
  },
  {
    titulo: 'ASISTENTE IA',
    flags: [
      { flag: 'menuIA', label: 'Menu Asistente IA (web)' },
      { flag: 'menuIAWhatsapp', label: 'Asistente IA por WhatsApp' },
    ],
  },
];

const TODOS_LOS_FLAGS = GRUPOS.flatMap((g) => g.flags.map((f) => f.flag));

function estadoInicial(usuario) {
  const estado = { role: usuario.role || 'staff', whatsapp_phone: usuario.whatsapp_phone || '' };
  for (const flag of TODOS_LOS_FLAGS) estado[flag] = usuario[flag] === true;
  return estado;
}

/**
 * Modal "Permisos de {nombre} {apellido}" — réplica funcional del modal de
 * `IbuFront/src/components/layout/Login/ListadoUsuarios.jsx` (mismos ~43 flags reales,
 * agrupados igual que el backend), más el selector `role` (admin/staff) que V1 no
 * tenía (agregado en V2, ver `user.model.js`). Guarda con
 * `PATCH /usuarios/:id/permisos` (mismo shape que `usuarios.validator.js#permisosValidator`
 * — flags booleanos + `role` opcional).
 */
export default function UsuarioPermisosModal({ usuario, onClose, onGuardado }) {
  const [valores, setValores] = useState(() => estadoInicial(usuario));
  const [guardando, setGuardando] = useState(false);

  const marcar = (flag) => (e) => setValores((v) => ({ ...v, [flag]: e.target.checked }));

  async function guardar() {
    setGuardando(true);
    try {
      // `PATCH /:id/permisos` (permisosValidator) SOLO acepta `role` + los flags de la
      // whitelist (`FLAGS_PERMISO`) — `whatsapp_phone` no está ahí (vive en
      // `editarValidator`, datos generales), así que va en una llamada aparte para no
      // romper la validación estricta del backend (`.min(1)` sin `unknown(true)`).
      const payloadPermisos = { role: valores.role };
      for (const flag of TODOS_LOS_FLAGS) payloadPermisos[flag] = valores[flag];
      await apiClient.patch(`/usuarios/${usuario._id}/permisos`, payloadPermisos);

      if ((valores.whatsapp_phone || '') !== (usuario.whatsapp_phone || '')) {
        await apiClient.patch(`/usuarios/${usuario._id}`, { whatsapp_phone: valores.whatsapp_phone || '' });
      }

      Swal.fire({ icon: 'success', title: 'Permisos actualizados', timer: 1200, showConfirmButton: false });
      onGuardado();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'No se pudieron guardar los permisos', text: error.message });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Permisos de ${usuario.nombre} ${usuario.apellido}`}
      width="max-w-6xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-outline">Cerrar</button>
          <button type="button" onClick={guardar} disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando...' : 'Guardar'}
          </button>
        </>
      )}
    >
      <div className="space-y-5">
        <div className="max-w-xs">
          <label className="label">Rol</label>
          <select
            value={valores.role}
            onChange={(e) => setValores((v) => ({ ...v, role: e.target.value }))}
            className="select-base"
          >
            <option value="staff">Staff</option>
            <option value="admin">Admin (bypass total de permisos)</option>
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* `min-w-0` en el `fieldset` de abajo: los `<fieldset>` tienen un
              `min-width` intrínseco basado en su contenido que el navegador NO
              reduce dentro de un grid/flex (a diferencia de un `<div>`) -- por eso,
              sin esto, el fieldset de "Contenedores" se ensanchaba a su ancho de
              contenido completo (569px) y se montaba encima de la columna vecina
              "Finanzas CxC", aunque el `<span>` de adentro ya tuviera `min-w-0
              break-words`. Bug real reportado por el usuario probando en el
              navegador 2026-09-28 -- confirmado midiendo `getBoundingClientRect()`
              de cada fieldset en vivo: se solapaban ~287px. */}
          {GRUPOS.map((grupo) => (
            <fieldset key={grupo.titulo} className="min-w-0 border border-slate-200 rounded-md p-3">
              <legend className="text-sm font-semibold text-slate-700 px-1">{grupo.titulo}</legend>
              <div className="space-y-1.5 mt-1">
                {grupo.flags.map(({ flag, label }) => (
                  <label key={flag} className="flex items-start gap-2 text-sm">
                    <input type="checkbox" className="mt-0.5 shrink-0" checked={!!valores[flag]} onChange={marcar(flag)} />
                    {/* `min-w-0` es necesario: un hijo `flex` sin él no encoge por debajo
                        de su ancho de contenido, así que un texto largo se desbordaba
                        sobre la columna vecina en vez de hacer wrap (bug real reportado
                        por el usuario probando en el navegador 2026-09-28). */}
                    <span className="min-w-0 break-words">{label}</span>
                  </label>
                ))}
                {grupo.titulo === 'Asistente IA' && (
                  <div className="pt-1">
                    <label className="label">Teléfono WhatsApp (staff)</label>
                    <input
                      type="text"
                      placeholder="10 dígitos, sin lada país"
                      value={valores.whatsapp_phone}
                      onChange={(e) => setValores((v) => ({ ...v, whatsapp_phone: e.target.value }))}
                      className="input-base"
                    />
                  </div>
                )}
              </div>
            </fieldset>
          ))}
        </div>
      </div>
    </Modal>
  );
}
